import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { ContentItem, ContentKind } from "@/lib/insights";

/** Canonical site origin used in canonical and og:url. */
const RAW_SITE_ORIGIN =
  (import.meta.env.VITE_SITE_ORIGIN as string | undefined) || "https://www.deepc.ai";
/** Canonical site origin used in canonical and og:url. No trailing slash. */
export const SITE_ORIGIN = RAW_SITE_ORIGIN.replace(/\/$/, "");

// NOTE: SITEWIDE_TITLE / SITEWIDE_DESCRIPTION / SITEWIDE_OG_IMAGE must stay in
// sync with the matching constants in scripts/seo-shared.mjs. The .mjs/.ts
// boundary prevents a shared import at prerender time; long-term fix is to
// render the prerender head via HelmetProvider/renderStatic (drops that copy).
export const SITEWIDE_TITLE =
  "deepc - Clinical AI Infrastructure for Health Systems";
export const SITEWIDE_DESCRIPTION =
  "Clinical AI infrastructure for health systems to deploy, monitor, and govern AI workflows across existing clinical and operational systems.";
/** Sitewide fallback social image (matches index.html). Used as last-resort og:image. */
export const SITEWIDE_OG_IMAGE =
  "https://deepc.ai/__l5e/assets-v1/4c7e8b4a-33e8-4236-a80c-f45eb3d49ee6/deepc-og.png";

function absoluteUrl(value: string | null | undefined): string {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : new URL(value, SITE_ORIGIN).toString();
}

export interface SeoTemplate {
  collection_key: string;
  title_template: string;
  description_template: string;
  og_title_template: string | null;
  og_description_template: string | null;
  og_image_url: string | null;
  noindex?: boolean | null;
}

/** Collections that can have an SEO template defined. */
export const SEO_COLLECTIONS: { key: string; label: string }[] = [
  { key: "insights.blog", label: "Blog" },
  { key: "insights.news", label: "News" },
  { key: "insights.event", label: "Events" },
  { key: "insights.webinar", label: "Media" },
  { key: "insights.evidence", label: "Evidence" },
  { key: "insights.other", label: "Other insights" },
  { key: "release_notes", label: "Release notes" },
];

/** Tokens users can insert into templates. */
export const SEO_TOKENS = [
  { key: "title", label: "Title" },
  { key: "summary", label: "Summary (falls back to subtitle)" },
  { key: "subtitle", label: "Subtitle" },
  { key: "category", label: "Category" },
  { key: "author_name", label: "Author" },
  { key: "event_starts_at", label: "Event date" },
] as const;

export function collectionKeyForInsight(kind: ContentKind): string {
  return `insights.${kind}`;
}

type Tokens = Record<string, string | null | undefined>;

export type SeoItem = Pick<
  ContentItem,
  "title" | "summary" | "subtitle" | "category" | "author_name" | "event_starts_at"
> & {
  /** Per-item hero/cover image — used as default og:image. */
  itemImage?: string | null;
};

function itemTokens(item: SeoItem): Tokens {
  const date = item.event_starts_at
    ? new Date(item.event_starts_at).toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";
  const summaryOrSubtitle = item.summary?.trim() || item.subtitle?.trim() || "";
  return {
    title: item.title ?? "",
    summary: summaryOrSubtitle,
    subtitle: item.subtitle ?? "",
    category: item.category ?? "",
    author_name: item.author_name ?? "",
    event_starts_at: date,
  };
}

/** Replace {token} placeholders in a template string. */
export function applyTemplate(template: string | null | undefined, tokens: Tokens): string {
  if (!template) return "";
  return template.replace(/\{(\w+)\}/g, (_m, k) => {
    const v = tokens[k];
    return v == null ? "" : String(v);
  }).trim();
}

export interface ResolvedSeo {
  title: string;
  description: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  noindex: boolean;
}

export function resolveSeo(
  template: SeoTemplate | null | undefined,
  item: SeoItem,
): ResolvedSeo {
  const t = itemTokens(item);
  const title = applyTemplate(template?.title_template ?? "{title}", t) || item.title || "";
  const description =
    applyTemplate(template?.description_template ?? "{summary}", t) || t.summary || "";
  const ogTitle =
    applyTemplate(template?.og_title_template ?? template?.title_template ?? "{title}", t) || title;
  const ogDescription =
    applyTemplate(
      template?.og_description_template ?? template?.description_template ?? "{summary}",
      t,
    ) || description;
  // Fallback chain: template override → item hero → sitewide
  const ogImage = absoluteUrl(template?.og_image_url || item.itemImage || SITEWIDE_OG_IMAGE);
  return {
    title,
    description,
    ogTitle,
    ogDescription,
    ogImage,
    noindex: Boolean(template?.noindex),
  };
}

/** Fetch a single template by collection key. */
export function useCollectionSeoTemplate(collectionKey: string | undefined) {
  return useQuery({
    queryKey: ["seo-template", collectionKey],
    enabled: Boolean(collectionKey),
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SeoTemplate | null> => {
      const { data, error } = await supabase
        .from("collection_seo_templates")
        .select(
          "collection_key, title_template, description_template, og_title_template, og_description_template, og_image_url, noindex",
        )
        .eq("collection_key", collectionKey!)
        .maybeSingle();
      if (error) throw error;
      return (data as SeoTemplate | null) ?? null;
    },
  });
}
